"use client"

import { useRef, useMemo, useState } from "react"
import { cn } from "@/lib/utils"
import { ContextMenuTrigger } from "@/components/ui/context-menu"
import { DraggableItem } from "./draggable-item"
import { ImageContextMenu } from "./image-context-menu"
import type { ImageItem, Position, Size } from "./types"
import Image from "next/image"

interface PageCanvasProps {
  pageWidth: number
  pageHeight: number
  items: ImageItem[]
  selectedItemId: string | null
  scale?: number
  showGrid?: boolean
  margins?: number
  disabled?: boolean
  onItemPositionChange: (id: string, position: Position) => void
  onItemSizeChange: (id: string, size: Size) => void
  onItemRotationChange: (id: string, rotation: number) => void
  onSelectItem: (id: string | null) => void
  onDeleteItem: (id: string) => void
  onDuplicateItem: (id: string) => void
  onBringForward: (id: string) => void
  onSendBackward: (id: string) => void
  onBringToFront: (id: string) => void
  onSendToBack: (id: string) => void
  onFlipHorizontal?: (id: string) => void
  onFlipVertical?: (id: string) => void
  onResetSize?: (id: string) => void
  onDropImages?: (files: File[], position: Position) => void
  onDropFromTray?: (imageId: string, position: Position) => void
}

export function PageCanvas({
  pageWidth,
  pageHeight,
  items,
  selectedItemId,
  scale = 1,
  showGrid = false,
  margins = 0,
  disabled = false,
  onItemPositionChange,
  onItemSizeChange,
  onItemRotationChange,
  onSelectItem,
  onDeleteItem,
  onDuplicateItem,
  onBringForward,
  onSendBackward,
  onBringToFront,
  onSendToBack,
  onFlipHorizontal,
  onFlipVertical,
  onResetSize,
  onDropImages,
  onDropFromTray,
}: PageCanvasProps) {
  const canvasRef = useRef<HTMLDivElement>(null)
  const [isDragOver, setIsDragOver] = useState(false)

  const scaledDimensions = useMemo(
    () => ({
      width: pageWidth * scale,
      height: pageHeight * scale,
    }),
    [pageWidth, pageHeight, scale]
  )

  const selectedItem = items.find((item) => item.id === selectedItemId)

  const handleCanvasClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget || (e.target as HTMLElement).dataset.canvas) {
      onSelectItem(null)
    }
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = "copy"
    setIsDragOver(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)

    const rect = canvasRef.current?.getBoundingClientRect()
    if (!rect) return

    const position = {
      x: (e.clientX - rect.left) / scale,
      y: (e.clientY - rect.top) / scale,
    }

    const imageId = e.dataTransfer.getData("application/x-image-id")
    if (imageId && onDropFromTray) {
      onDropFromTray(imageId, position)
      return
    }

    if (!onDropImages) return

    const files = Array.from(e.dataTransfer.files).filter((f) => f.type.startsWith("image/"))
    if (files.length === 0) return

    onDropImages(files, position)
  }

  const handleFitToPage = () => {
    if (!selectedItem) return
    const contentWidth = pageWidth - margins * 2
    const contentHeight = pageHeight - margins * 2
    const aspectRatio = selectedItem.originalWidth / selectedItem.originalHeight

    let newWidth = contentWidth
    let newHeight = contentWidth / aspectRatio

    if (newHeight > contentHeight) {
      newHeight = contentHeight
      newWidth = contentHeight * aspectRatio
    }

    onItemSizeChange(selectedItem.id, { width: newWidth, height: newHeight })
    onItemPositionChange(selectedItem.id, {
      x: margins + (contentWidth - newWidth) / 2,
      y: margins + (contentHeight - newHeight) / 2,
    })
  }

  const handleCenterHorizontal = () => {
    if (!selectedItem) return
    onItemPositionChange(selectedItem.id, {
      x: (pageWidth - selectedItem.size.width) / 2,
      y: selectedItem.position.y,
    })
  }

  const handleCenterVertical = () => {
    if (!selectedItem) return
    onItemPositionChange(selectedItem.id, {
      x: selectedItem.position.x,
      y: (pageHeight - selectedItem.size.height) / 2,
    })
  }

  const handleAlignLeft = () => {
    if (!selectedItem) return
    onItemPositionChange(selectedItem.id, {
      x: margins,
      y: selectedItem.position.y,
    })
  }

  const handleAlignRight = () => {
    if (!selectedItem) return
    onItemPositionChange(selectedItem.id, {
      x: pageWidth - margins - selectedItem.size.width,
      y: selectedItem.position.y,
    })
  }

  const handleAlignTop = () => {
    if (!selectedItem) return
    onItemPositionChange(selectedItem.id, {
      x: selectedItem.position.x,
      y: margins,
    })
  }

  const handleAlignBottom = () => {
    if (!selectedItem) return
    onItemPositionChange(selectedItem.id, {
      x: selectedItem.position.x,
      y: pageHeight - margins - selectedItem.size.height,
    })
  }

  const handleRotate90 = () => {
    if (!selectedItem) return
    onItemRotationChange(selectedItem.id, (selectedItem.rotation + 90) % 360)
  }

  const handleRotate90CCW = () => {
    if (!selectedItem) return
    onItemRotationChange(selectedItem.id, (selectedItem.rotation - 90 + 360) % 360)
  }

  return (
    <div className="overflow-auto flex-1 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-muted/30 via-muted/50 to-muted/70 flex items-center justify-center p-8">
      <div
        ref={canvasRef}
        data-canvas="true"
        className={cn(
          "relative bg-white shadow-xl transition-all rounded-sm",
          showGrid &&
            "bg-[length:20px_20px] bg-[image:linear-gradient(to_right,#e5e7eb_1px,transparent_1px),linear-gradient(to_bottom,#e5e7eb_1px,transparent_1px)]",
          isDragOver && "ring-4 ring-primary ring-offset-4"
        )}
        style={{
          width: scaledDimensions.width,
          height: scaledDimensions.height,
          minWidth: scaledDimensions.width,
          minHeight: scaledDimensions.height,
        }}
        onClick={handleCanvasClick}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {margins > 0 && (
          <div
            data-canvas="true"
            className="absolute border border-dashed border-blue-300/50 pointer-events-none"
            style={{
              left: margins * scale,
              top: margins * scale,
              width: (pageWidth - margins * 2) * scale,
              height: (pageHeight - margins * 2) * scale,
            }}
          />
        )}

        {items
          .sort((a, b) => a.zIndex - b.zIndex)
          .map((item) => (
            <ImageContextMenu
              key={item.id}
              onDelete={() => onDeleteItem(item.id)}
              onDuplicate={() => onDuplicateItem(item.id)}
              onFitToPage={handleFitToPage}
              onCenterHorizontal={handleCenterHorizontal}
              onCenterVertical={handleCenterVertical}
              onAlignLeft={handleAlignLeft}
              onAlignRight={handleAlignRight}
              onAlignTop={handleAlignTop}
              onAlignBottom={handleAlignBottom}
              onBringForward={() => onBringForward(item.id)}
              onSendBackward={() => onSendBackward(item.id)}
              onBringToFront={() => onBringToFront(item.id)}
              onSendToBack={() => onSendToBack(item.id)}
              onRotate90={handleRotate90}
              onRotate90CCW={handleRotate90CCW}
              onFlipHorizontal={onFlipHorizontal ? () => onFlipHorizontal(item.id) : undefined}
              onFlipVertical={onFlipVertical ? () => onFlipVertical(item.id) : undefined}
              onResetSize={onResetSize ? () => onResetSize(item.id) : undefined}
            >
              <ContextMenuTrigger asChild>
                <div>
                  <DraggableItem
                    id={item.id}
                    position={{ x: item.position.x * scale, y: item.position.y * scale }}
                    size={{ width: item.size.width * scale, height: item.size.height * scale }}
                    rotation={item.rotation}
                    selected={item.id === selectedItemId}
                    disabled={disabled}
                    aspectRatio={item.originalWidth / item.originalHeight}
                    containerRef={canvasRef}
                    onPositionChange={(id, pos) =>
                      onItemPositionChange(id, { x: pos.x / scale, y: pos.y / scale })
                    }
                    onSizeChange={(id, size) =>
                      onItemSizeChange(id, { width: size.width / scale, height: size.height / scale })
                    }
                    onSelect={onSelectItem}
                    onDoubleClick={handleFitToPage}
                  >
                    <Image
                      src={item.src}
                      alt={item.fileName}
                      fill
                      className="object-contain pointer-events-none"
                      style={{
                        transform: `${item.flipX ? "scaleX(-1)" : ""} ${item.flipY ? "scaleY(-1)" : ""}`.trim() || undefined,
                        opacity: (item.opacity ?? 100) / 100,
                      }}
                      draggable={false}
                    />
                  </DraggableItem>
                </div>
              </ContextMenuTrigger>
            </ImageContextMenu>
          ))}

        {items.length === 0 && !disabled && (
          <div
            data-canvas="true"
            className="absolute inset-0 flex items-center justify-center pointer-events-none"
          >
            <div className="text-center space-y-4 max-w-[200px]">
              <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-muted/80 to-muted/40 mx-auto flex items-center justify-center shadow-inner">
                <svg className="w-10 h-10 text-muted-foreground/40" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <div className="space-y-1">
                <p className="text-sm font-medium text-muted-foreground">Drop images here</p>
                <p className="text-xs text-muted-foreground/60">Drag from the image panel or drop files directly</p>
              </div>
            </div>
          </div>
        )}

        {isDragOver && (
          <div className="absolute inset-0 bg-primary/10 backdrop-blur-[1px] flex items-center justify-center pointer-events-none z-50 border-2 border-dashed border-primary rounded-sm">
            <div className="bg-primary text-primary-foreground px-4 py-2 rounded-lg font-medium shadow-lg animate-pulse">
              Drop to add image
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
